// The Shipping page's "Awaiting Pickup" tab (pages/ShippingQueuePage.jsx) confirms a pickup through the SAME action
// as the Sales Order page and the phone: palletActions.confirmPickup (load-out checklist first: pull the beacon,
// check the pallet off as it is loaded; then the PIN; one POST carrying load_checklist). Before 2026-10-27 the tab
// posted {pin} only, so the checklist was skipped and the pallet's beacon was never released.
//
// A row of GET /shipping/awaiting-pickup is one staged pallet; confirmPickup wants a plan, so the row is turned into
// the little plan it reads (plan_id, order_number, the one pallet). The Shipping page is a shop-floor page: the
// person handing the pallet over types THEIR PIN (no admin-session autofill: a PC left in an admin session must not
// sign the pickup as the admin). A face at a station still names the operator.
import { confirmPickup } from './palletActions';

/** The pallet's name: PLT-XXXX when the API sends short_id, else P<n> (as palletDisplayName), else P#<id>. */
export function pickupPalletLabel(row) {
  if (!row) return '';
  if (row.short_id) return row.short_id;
  if (row.pallet_num != null) return `P${row.pallet_num}`;
  return `P#${row.pallet_id}`;
}

/** The plan confirmPickup reads, built from one awaiting-pickup row. */
export function pickupRowPlan(row) {
  return {
    plan_id: row.plan_id,
    order_number: row.order_number,
    pallets: [{ pallet_id: row.pallet_id, pallet_num: row.pallet_num ?? null, short_id: pickupPalletLabel(row) }],
  };
}

/** The PIN options a shop-floor prompt gets (Phase 1, 2026-10-27; the same rule as the package guide's identityUi):
 *  an IDENTITY prompt (who is doing it) never takes the admin session's PIN and may use a face; an APPROVAL prompt
 *  (opts.face === false: a manager's override) stays typed and face-free, as its caller asked. Before, approvals
 *  got face: true here, so a face could have answered a manager override (the API refuses it, and the prompt loops). */
export function identityPinOpts(opts) {
  const o = opts || {};
  if (o.face === false) return { ...o, autofill: false, face: false };
  return { ...o, autofill: false, face: true };
}

/** `ui` with a PIN prompt that always asks (never the admin session's PIN) and names the order + customer. */
export function operatorPinUi(ui, row) {
  const who = row ? [row.order_number, row.customer_name ? `(${row.customer_name})` : ''].filter(Boolean).join(' ') : '';
  return {
    ...ui,
    pin: (message, opts) => ui.pin(who ? `${who}: ${message}` : message, identityPinOpts(opts)),
  };
}

/** Confirm pickup of one awaiting-pickup row. Resolves like confirmPickup: {done, message?}. */
export function confirmPickupFromQueue({ row, ui, setBusy, setMessage, reload }) {
  if (!row || row.plan_id == null || row.pallet_id == null) {
    return Promise.resolve({ done: false, message: 'This row has no plan or pallet: open the sales order instead.' });
  }
  return confirmPickup({
    plan: pickupRowPlan(row),
    palletId: row.pallet_id,
    ui: operatorPinUi(ui, row),
    setBusy,
    setMessage,
    reloadPlan: reload,
  });
}
